import React from 'react';
import { Box, Paper, Menu, MenuItem as MuiMenuItem, Dialog, DialogTitle, DialogContent, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TuneIcon from '@mui/icons-material/Tune';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ConversationList from './ConversationList';
import ChatMessages from './ChatMessages';
import MessageInput from './MessageInput';
import TeachingPanel from './TeachingPanel';
import { useChat } from './hooks/useChat';

const centerPaperSx = { p: 0, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 };

const TeachingChatPageView: React.FC = () => {
  const chat = useChat();
  const closePreview = () => chat.setPreview({ open: false, src: '', alt: '' });
  const docPreview = !chat.preview.src
    ? chat.composeAttachments.find((att) => att.filename === chat.preview.alt && att.type === 'document')
    : null;
  const isGuided = chat.mode === 'guided';
  const gridSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: isGuided ? '280px 1fr 320px' : '280px 1fr' },
    // 50px == TopBar height, which <main> already offsets with mt: '50px'.
    gap: 2, pt: 0, height: 'calc(100vh - 50px)', overflow: 'hidden',
  };

  return (
    <>
      <Box sx={gridSx}>
        <ConversationList
          conversations={chat.conversations}
          convQuery={chat.convQuery}
          setConvQuery={chat.setConvQuery}
          activeConversationId={chat.activeConversationId}
          selectConversation={chat.selectConversation}
          menuAnchorEl={chat.menuAnchorEl}
          menuConvId={chat.menuConvId}
          openConvMenu={chat.openConvMenu}
          closeConvMenu={chat.closeConvMenu}
          renameConversation={chat.renameConversation}
          archiveConversation={chat.archiveConversation}
          deleteConversation={chat.deleteConversation}
        />
        <Paper elevation={1} sx={centerPaperSx}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={chat.mode}
              onChange={(_e, value) => { if (value) chat.setMode(value); }}
            >
              <ToggleButton value="guided" sx={{ textTransform: 'none', px: 2 }}>
                <TuneIcon fontSize="small" sx={{ mr: 0.75 }} />
                Guided
              </ToggleButton>
              <ToggleButton value="free" sx={{ textTransform: 'none', px: 2 }}>
                <ChatBubbleOutlineIcon fontSize="small" sx={{ mr: 0.75 }} />
                Open Chat
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <ChatMessages
            actionError={chat.actionError}
            setActionError={chat.setActionError}
            messages={chat.messages}
            currentStream={chat.currentStream}
            isLoading={chat.isLoading}
            scrollContainerRef={chat.scrollContainerRef}
            messagesEndRef={chat.messagesEndRef}
            isNearBottom={chat.isNearBottom}
            assistantContentRefs={chat.assistantContentRefs}
            handlePrintAssistant={chat.handlePrintAssistant}
            openDownloadMenu={chat.openDownloadMenu}
            setPreview={chat.setPreview}
            onStarterPrompt={chat.setInput}
          />
          <MessageInput
            input={chat.input}
            setInput={chat.setInput}
            sendMessage={chat.sendMessage}
            isLoading={chat.isLoading}
            stopGenerating={chat.stopGenerating}
            abortRef={chat.abortRef}
            fileInputRef={chat.fileInputRef}
            handlePaste={chat.handlePaste}
            handleDragOver={chat.handleDragOver}
            handleDragLeave={chat.handleDragLeave}
            handleDrop={chat.handleDrop}
            isDragOver={chat.isDragOver}
            composeAttachments={chat.composeAttachments}
            setComposeAttachments={chat.setComposeAttachments}
            handleFiles={chat.handleFiles}
            formatFileSize={chat.formatFileSize}
            setPreview={chat.setPreview}
          />
        </Paper>
        {isGuided && (
        <TeachingPanel
          gradeLevel={chat.gradeLevel}
          subject={chat.subject}
          updateSettings={chat.updateSettings}
          loadingGradeLevels={chat.loadingGradeLevels}
          availableGradeLevels={chat.availableGradeLevels}
          selectedStrand={chat.selectedStrand}
          setSelectedStrand={chat.setSelectedStrand}
          availableStrands={chat.availableStrands}
          selectedStandards={chat.selectedStandards}
          setSelectedStandards={chat.setSelectedStandards}
          strandFilteredStandards={chat.strandFilteredStandards}
          capitalizeFirstLetter={chat.capitalizeFirstLetter}
          loadingStandards={chat.loadingStandards}
          standardLoading={chat.standardLoading}
          messages={chat.messages}
          personalizationText={chat.personalizationText}
          aiSuggestLoading={chat.aiSuggestLoading}
          prioritizedContextualSuggestions={chat.prioritizedContextualSuggestions}
          visibleContextualSuggestions={chat.visibleContextualSuggestions}
          hiddenContextualSuggestions={chat.hiddenContextualSuggestions}
          hasMoreContextual={chat.hasMoreContextual}
          showMoreContextual={chat.showMoreContextual}
          setShowMoreContextual={chat.setShowMoreContextual}
          setInput={chat.setInput}
          setAiSuggested={chat.setAiSuggested}
        />
        )}
      </Box>
      <Menu anchorEl={chat.downloadMenuAnchor} open={Boolean(chat.downloadMenuAnchor)} onClose={chat.closeDownloadMenu} keepMounted>
        <MuiMenuItem onClick={() => { if (chat.downloadMenuForIdx != null) chat.handleDownloadAssistantPDF(chat.downloadMenuForIdx); chat.closeDownloadMenu(); }}>
          <DownloadIcon sx={{ mr: 1 }} /> Download PDF
        </MuiMenuItem>
        <MuiMenuItem onClick={() => { if (chat.downloadMenuForIdx != null) chat.handleDownloadAssistantWord(chat.downloadMenuForIdx); chat.closeDownloadMenu(); }}>
          <DownloadIcon sx={{ mr: 1 }} /> Download Word
        </MuiMenuItem>
      </Menu>
      <Dialog open={chat.preview.open} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogTitle>{chat.preview.alt}</DialogTitle>
        <DialogContent>
          {chat.preview.src ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img src={chat.preview.src} alt={chat.preview.alt} style={{ maxWidth: '100%', height: 'auto' }} />
            </Box>
          ) : docPreview ? (
            <Box sx={{ backgroundColor: 'grey.50', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', maxHeight: '400px', overflow: 'auto' }}>
              <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem', lineHeight: 1.5 }}>
                {docPreview.data}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No content available for this file.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TeachingChatPageView;
